import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (v) => (Number(v || 0)).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const Card = ({ label, value, hint, testid }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-testid={testid}>
    <p className="text-white/40 text-[11px] tracking-wider uppercase">{label}</p>
    <p className="text-gold font-bold text-2xl mt-1">{value}</p>
    {hint && <p className="text-white/40 text-xs mt-1">{hint}</p>}
  </div>
);

const Section = ({ title, children, testid }) => (
  <div className="mt-8" data-testid={testid}>
    <h3 className="font-heading text-sm tracking-widest text-white/60 uppercase mb-3">{title}</h3>
    <div className="bg-white/5 border border-white/10 rounded-lg divide-y divide-white/5">{children}</div>
  </div>
);

const Row = ({ label, value, hint }) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3">
    <span className="text-white/80 text-sm">{label}</span>
    <span className="text-right">
      <span className="text-gold font-semibold text-sm">{value}</span>
      {hint && <span className="block text-white/40 text-xs">{hint}</span>}
    </span>
  </div>
);

const originRows = (map) => {
  const entries = Object.entries(map || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <Row label="Nessun record nel periodo" value="—" />;
  return entries.map(([k, v]) => <Row key={k} label={k} value={v} />);
};

export const SiteStatsPanel = ({ data, loading, month, setMonth, onRefresh, token }) => {
  const [sending, setSending] = React.useState(false);
  const p = data?.mese;
  const w = data?.settimana;
  const wp = data?.settimana_precedente;

  const sendProva = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/stats/prova`, { month }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Riepilogo di prova inviato: ${res.data.prodotti_n} vendite, ${eur(res.data.prodotti_revenue)}`);
    } catch (e) {
      toast.error('Invio riepilogo di prova fallito');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="site-stats-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-heading text-xl text-white">STATISTICHE SITO</h2>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => { setMonth(e.target.value); onRefresh(e.target.value); }}
            className="bg-white/5 border border-white/10 text-white px-3 py-2 rounded text-sm"
            data-testid="stats-month-input"
          />
          <button
            onClick={() => onRefresh(month)}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-4 py-2 rounded text-sm transition-all"
            data-testid="stats-refresh-btn"
          >
            <RefreshCw size={14} /> Aggiorna
          </button>
          <button
            onClick={sendProva}
            disabled={sending}
            className="flex items-center gap-2 bg-gold/15 hover:bg-gold/25 border border-gold/40 text-gold px-4 py-2 rounded text-sm transition-all disabled:opacity-50"
            data-testid="stats-send-prova-btn"
          >
            <Mail size={14} /> {sending ? 'Invio...' : 'Invia riepilogo di prova'}
          </button>
        </div>
      </div>

      {loading && <p className="text-white/50 text-sm">Calcolo in corso...</p>}

      {!loading && p && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card testid="stats-card-incasso" label="Incasso mese (IVA compresa)" value={eur(p.incassi.lordo)} hint={`${eur(p.incassi.netto)} al netto IVA`} />
            <Card testid="stats-card-vendite" label="Prodotti digitali" value={p.prodotti.n} hint={eur(p.prodotti.revenue)} />
            <Card testid="stats-card-visitatori" label="Visitatori unici" value={p.visite.visitatori} hint={`${p.visite.pagine_viste} pagine viste`} />
            <Card testid="stats-card-checkup" label="Check-up completati" value={p.checkup.completati} hint={`${p.checkup.avviati} avviati · ${p.checkup.abbandonati} abbandonati`} />
          </div>
          <p className="text-white/40 text-xs mt-3">
            Periodo: {p.label_start} – {p.label_end} · record esclusi come test: {p.esclusi_test} · abbonamenti letti da {p.abbonamenti_fonte === 'stripe' ? 'Stripe' : 'archivio locale'}
          </p>

          <Section title="Prodotti digitali" testid="stats-section-prodotti">
            {p.prodotti.items.length === 0
              ? <Row label="Nessuna vendita nel periodo" value="—" />
              : p.prodotti.items.map((it) => <Row key={it.titolo} label={it.titolo} value={it.n} hint={eur(it.revenue)} />)}
          </Section>

          <Section title="Abbonamenti forfettari" testid="stats-section-abbonamenti">
            {Object.entries(p.abbonamenti).map(([plan, r]) => (
              <Row key={plan} label={plan}
                value={`${r.nuovi} nuovi · ${r.disdetti} disdetti · ${r.attivi} attivi`}
                hint={`canoni attivi ${eur(r.canone_mensile)}/mese`} />
            ))}
            <Row label="Canoni incassati nel periodo" value={eur(p.canoni_incassati.revenue)} hint={`${p.canoni_incassati.n} addebiti`} />
          </Section>

          <Section title="Consulenze e servizi" testid="stats-section-servizi">
            {p.consulenze.items.map((it) => <Row key={it.titolo} label={it.titolo} value={it.n} hint={eur(it.revenue)} />)}
            {p.consulenze.items.length === 0 && <Row label="Consulenze a pagamento (97 € / 197 €)" value={0} />}
            <Row label="Valutazione CPB" value={p.cpb.n} hint={eur(p.cpb.revenue)} />
            <Row label="Check-up approfondito" value={p.checkup_approfondito.n} hint={eur(p.checkup_approfondito.revenue)} />
            <Row label="Portale di fatturazione" value={p.portale.n} hint={eur(p.portale.revenue)} />
          </Section>

          <Section title="Check-up e lead" testid="stats-section-lead">
            <Row label="Check-up avviati" value={p.checkup.avviati} />
            <Row label="Check-up completati" value={p.checkup.completati} />
            <Row label="Check-up abbandonati (con contatto)" value={p.checkup.abbandonati} />
            <Row label="Lead del libro" value={p.lead_libro} />
            <Row label="Richieste di contatto" value={p.contatti} />
          </Section>

          <div className="grid lg:grid-cols-2 gap-6">
            <Section title="Origine · vendite e abbonamenti" testid="stats-section-origine-vendite">
              {originRows({ ...p.origini.vendite })}
              <Row label="— abbonamenti —" value="" />
              {originRows(p.origini.abbonamenti)}
            </Section>
            <Section title="Origine · check-up e lead" testid="stats-section-origine-lead">
              {originRows(p.origini.checkup)}
              <Row label="— lead e contatti —" value="" />
              {originRows(p.origini.lead)}
            </Section>
          </div>

          <Section title="Visite per pagina" testid="stats-section-visite">
            {p.visite.per_pagina.length === 0
              ? <Row label="Nessuna visita registrata nel periodo" value="—" hint="il conteggio parte dall'attivazione" />
              : p.visite.per_pagina.map((v) => <Row key={v.pagina} label={v.pagina} value={v.views} hint={`${v.visitatori} visitatori`} />)}
          </Section>

          {w && (
            <Section title="Settimana chiusa vs precedente" testid="stats-section-settimana">
              <Row label={`Incasso (${w.label_start} – ${w.label_end})`} value={eur(w.incassi.lordo)} hint={`settimana prima ${eur(wp?.incassi?.lordo)}`} />
              <Row label="Prodotti digitali" value={w.prodotti.n} hint={`settimana prima ${wp?.prodotti?.n ?? 0}`} />
              <Row label="Visitatori unici" value={w.visite.visitatori} hint={`settimana prima ${wp?.visite?.visitatori ?? 0}`} />
              <Row label="Check-up completati" value={w.checkup.completati} hint={`settimana prima ${wp?.checkup?.completati ?? 0}`} />
              <Row label="Lead e contatti" value={w.lead_libro + w.contatti} hint={`settimana prima ${(wp?.lead_libro ?? 0) + (wp?.contatti ?? 0)}`} />
            </Section>
          )}
        </>
      )}
    </div>
  );
};
